// ============================================================================
// HAI Reporting Tool - Ward Management Component
// Admin UI for managing wards
// ============================================================================

import { useState, useEffect } from 'react';
import {
  getAllWards,
  getAllFacilities,
  createWard,
  updateWard,
  deleteWard
} from '@/services/admin.service';
import type { Ward, Facility } from '@/types';

export function WardManagement() {
  const [wards, setWards] = useState<Ward[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWard, setEditingWard] = useState<Ward | null>(null);
  const [formData, setFormData] = useState({
    facilityId: '',
    name: '',
    description: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wardsData, facilitiesData] = await Promise.all([
        getAllWards(),
        getAllFacilities()
      ]);
      setWards(wardsData);
      setFacilities(facilitiesData);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (editingWard) {
        await updateWard(editingWard.id, formData);
        setSuccess('Ward updated successfully');
      } else {
        await createWard(formData);
        setSuccess('Ward created successfully');
      }

      setFormData({ facilityId: '', name: '', description: '' });
      setShowForm(false);
      setEditingWard(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save ward');
    }
  };

  const handleEdit = (ward: Ward) => {
    setEditingWard(ward);
    setFormData({
      facilityId: ward.facilityId,
      name: ward.name,
      description: ward.description
    });
    setShowForm(true);
  };

  const handleToggleActive = async (ward: Ward) => {
    try {
      await updateWard(ward.id, { isActive: !ward.isActive });
      setSuccess(`Ward ${ward.isActive ? 'deactivated' : 'activated'} successfully`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update ward');
    }
  };

  const handleDelete = async (ward: Ward) => {
    if (!confirm(`Are you sure you want to delete "${ward.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteWard(ward.id);
      setSuccess('Ward deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete ward');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingWard(null);
    setFormData({ facilityId: '', name: '', description: '' });
  };

  const getFacilityName = (facilityId: string) => {
    return facilities.find(f => f.id === facilityId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading wards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Wards</h3>
          <p className="text-sm text-gray-600">Manage hospital wards</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
            disabled={facilities.length === 0}
          >
            + Add Ward
          </button>
        )}
      </div>

      {facilities.length === 0 && (
        <div className="bg-warning-50 border border-warning-200 text-warning-700 px-4 py-3 rounded">
          Please create at least one facility before adding wards.
        </div>
      )}

      {/* Alert Messages */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card bg-gray-50">
          <h4 className="font-semibold mb-4">
            {editingWard ? 'Edit Ward' : 'Add New Ward'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facility *
              </label>
              <select
                value={formData.facilityId}
                onChange={(e) => setFormData({ ...formData, facilityId: e.target.value })}
                className="input"
                required
              >
                <option value="">Select a facility</option>
                {facilities.filter(f => f.isActive).map(facility => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name} ({facility.region})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ward Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                required
                placeholder="e.g., ICU, Medical Ward A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                rows={3}
                placeholder="Optional description of the ward"
              />
            </div>

            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary">
                {editingWard ? 'Update' : 'Create'} Ward
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Wards List */}
      <div className="card">
        {wards.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No wards found. Create your first ward to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ward Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {wards.map((ward) => (
                  <tr key={ward.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ward.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getFacilityName(ward.facilityId)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {ward.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ward.isActive
                            ? 'bg-success-100 text-success-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {ward.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(ward)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(ward)}
                        className="text-warning-600 hover:text-warning-900 mr-4"
                      >
                        {ward.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(ward)}
                        className="text-danger-600 hover:text-danger-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
